import type { ObservableMap } from 'mobx'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { FirestoreRoomMessageType } from '../../firestore/RoomMessage/schema'
import { SlideType } from '../../firestore/Slide/types'
import type { TranscriptMessage } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'

function sortTranscriptMessages(a: TranscriptMessage, b: TranscriptMessage) {
  const aTime = a.final && a.finalSort ? a.finalSort : a.id
  const bTime = b.final && b.finalSort ? b.finalSort : b.id
  return aTime > bTime ? 1 : -1
}

const periodicVerificationIntervalMS = 60_000

export class TranscriptController {
  meeting: MeetingCubit
  _messages: ObservableMap<number, TranscriptMessage> = observable.map({})
  readonly _startedAt: number = Date.now()
  userSeen = false
  userHeard = false
  verified = false
  speechDuration = 0
  lastUserTranscriptAt: number | null = null
  unmutedWithoutTranscriptMS = 0
  finalMessageVerificationTimeouts: Record<string, NodeJS.Timeout> = {}
  verificationInterval: NodeJS.Timeout | null = null
  periodicVerificationFailures = 0

  constructor(meeting: MeetingCubit) {
    this.meeting = meeting

    makeObservable(this, {
      userSeen: observable,
      userHeard: observable,
      speechDuration: observable,
      verified: observable,
      messages: computed,
      handleMessage: action,
      handleSpeakerChange: action,
      addSpeechDuration: action,
      restartVerification: action,
    })
  }

  initialize() {
    this.startPeriodicVerification()
  }

  dispose() {
    if (this.verificationInterval) clearInterval(this.verificationInterval)

    // clear any danling timeouts
    for (const timeout of Object.values(
      this.finalMessageVerificationTimeouts
    )) {
      clearTimeout(timeout)
    }
  }

  startPeriodicVerification() {
    // every minute, check if seen and heard match, if they don't for 5 minutes, log an error
    this.verificationInterval = setInterval(() => {
      this.checkUnmutedWithoutTranscript()

      // if the two states don't match, we're in failed state
      // now this is possible to happen and be fine - the user said something
      // and we simply haven't received the transcript yet
      // but if it happens for a long time, we log an error
      if (this.userSeen !== this.userHeard) {
        this.periodicVerificationFailures++
        if (this.periodicVerificationFailures > 5) {
          this.meeting.logEvent(
            'meeting_transcript_verification_errors_exceeded'
          )
          this.restartVerification()
        }
      } else {
        // if both are true or both are false, we reset the failures
        this.periodicVerificationFailures = 0
      }
    }, periodicVerificationIntervalMS)
  }

  // Garbage-mic detection
  // (see docs/incidents/support/2026-07-05-solo-room-no-transcription.md):
  // a broken capture device can stream noise that passes every energy-based
  // check while carrying no speech, so the speech-duration verification above
  // may never arm. In a solo room the student is expected to talk, so a long
  // stretch of being unmuted without a single transcript is a strong signal
  // that nobody can hear them. Mute state is sampled once per tick, which is
  // deliberately coarse — a mid-session mute simply restarts the window.
  checkUnmutedWithoutTranscript() {
    const problemDurationMS =
      this.meeting.repository.featureFlags.data
        .unmutedNoTranscriptProblemDurationMS
    if (!problemDurationMS) return
    if (!this.meeting.isInSoloMode) return
    if (this.meeting.qa_transcriptProcessingDisabled) return

    const transcriptSeenRecently =
      this.lastUserTranscriptAt !== null &&
      Date.now() - this.lastUserTranscriptAt < problemDurationMS
    if (this.meeting.isMuted || transcriptSeenRecently) {
      this.unmutedWithoutTranscriptMS = 0
      return
    }

    // Only count time on discussion slides — a silent student on a quiz or
    // video slide is expected. Pauses rather than resets: minutes already
    // accumulated on discussion slides stay valid evidence across a quiz
    // slide in between.
    if (this.meeting.currentSlide?.type !== SlideType.discussion) return

    this.unmutedWithoutTranscriptMS += periodicVerificationIntervalMS
    if (this.unmutedWithoutTranscriptMS < problemDurationMS) return

    // Reset so a full window must elapse again before the next emit —
    // MeetingPage only shows the dialog on a repeated problem within its
    // own wait window.
    this.unmutedWithoutTranscriptMS = 0
    this.meeting.localCommands.emit('transcriptProblem')
    this.meeting.logEvent('meeting_unmuted_no_transcript_problem', {
      unmuted_duration: problemDurationMS,
    })
  }

  handleMessage(message: TranscriptMessage) {
    if (this.meeting.qa_transcriptProcessingDisabled) return

    // Forward the local participant's new or changed, non-empty captions to
    // Soundcheck, which owns the transcript-flow health decision. The comparison
    // against the existing caption stays here; chat, other participants and exact
    // duplicate updates do not count.
    if (
      message.identity === this.meeting.repository.uid &&
      !message.isChat &&
      message.transcript.trim().length > 0
    ) {
      const priorKey = message.final
        ? message.finalSort || message.id
        : message.id
      const prior = this._messages.get(priorKey)
      const changed =
        !prior ||
        prior.transcript !== message.transcript ||
        prior.final !== message.final
      if (changed) this.meeting.soundcheck.reportTranscript()
    }

    if (message.final) {
      if (message.identity === this.meeting.repository.uid) {
        this.meeting.logEvent('meeting_transcript_received_final', {
          message_id: message.id,
          final_sort: message.finalSort,
        })
      }
      // if message is final, remove the old by id
      // and store the new one by finalSort
      this._messages.delete(message.id)
      this._messages.set(message.finalSort || message.id, message)
    } else {
      if (!this._messages.has(message.id)) {
        // only inform of first time this arrives - this event is MINDLOWINGLY noisy
        this.meeting.logEvent('meeting_transcript_received', {
          message_id: message.id,
        })
      }
      this._messages.set(message.id, message)
    }

    // mark user as seen if we get a message about them
    if (message.identity === this.meeting.repository.uid) {
      this.userSeen = true
      this.lastUserTranscriptAt = Date.now()
      this.startFinalMessageVerificationTimeout(message)
    }

    // remove old messages
    if (this._messages.size > 10) {
      const array = Array.from(this._messages.values())
      const sorted = array.sort(sortTranscriptMessages)

      // find all message before last 10
      const toRemove = sorted.slice(0, sorted.length - 10)

      for (const message of toRemove) {
        this._messages.delete(message.id)
      }
    }
  }

  // When a message is received, we keep a progressively increasing timeout to verify that the final message is received
  // The goal is that we expect it to be final within 60 seconds of the first message
  startFinalMessageVerificationTimeout(message: TranscriptMessage) {
    // remove the old timeout
    if (this.finalMessageVerificationTimeouts[message.identity]) {
      clearTimeout(this.finalMessageVerificationTimeouts[message.identity])
    }

    // if the message is final, all is good, we don't need to verify
    if (message.final) return

    // wait 60 seconds with verification - this will be pushed back by any update on the message
    const timeout = setTimeout(() => {
      this.verifyFinalMessage(message.id)
    }, 60_000)

    this.finalMessageVerificationTimeouts[message.identity] = timeout
  }

  verifyFinalMessage(messageId: number) {
    const message = this._messages.get(messageId)

    if (!message) return // message is gone, we can't verify it

    if (!message.final) {
      this.meeting.logEvent('meeting_final_message_verification_failed', {
        message,
      })
    }
  }

  handleSpeakerChange(identity: string) {
    if (identity === this.meeting.repository.uid) {
      this.userHeard = true
    }
  }

  transcriptVerificationTimeout: NodeJS.Timeout | null = null
  transcriptVerificationFailed = false
  minDuration = 2000
  addSpeechDuration(duration: number) {
    // only add speech duration if it's above the minimum duration
    if (duration < this.minDuration) return
    this.speechDuration += duration

    // if we already failed, don't re-check
    if (this.transcriptVerificationFailed) return

    if (this.speechDuration > 10_000) {
      this.userHeard = true
      if (this.transcriptVerificationTimeout) return

      this.transcriptVerificationTimeout = setTimeout(() => {
        runInAction(() => {
          if (!this.userSeen) {
            // mark as failed - prevents more runs
            this.transcriptVerificationFailed = true
            this.meeting.localCommands.emit('transcriptProblem')
            this.meeting.logEvent('meeting_transcript_problem', {
              duration: this.speechDuration,
            })
            console.error('Transcript verification failed', {
              duration: this.speechDuration,
            })
          } else {
            // if seen, consider it verified
            this.meeting.localCommands.emit('transcriptSuccess')
            this.verified = true
            // reset periodic verification failures
            this.periodicVerificationFailures = 0
          }
          this.restartVerification()
        })
      }, this.transcriptVerificationTimeoutMS)
    }
  }

  get transcriptVerificationTimeoutMS(): number {
    // Server VAD (v2) only commits a segment after 800ms of silence,
    // so during continuous speech we won't receive any transcript until
    // the speaker pauses. The local LiveKit `isSpeakingChanged` is much
    // more sensitive and accumulates `speechDuration` rapidly, so a 10s
    // verification window fires before v2 has had a chance to finalize.
    // Bump to 60s for v2 to give the server time to emit a final.
    if (this.meeting.roomState.data?.transcriberVersion === 'v2') {
      return 60_000
    }
    return 10_000
  }

  restartVerification() {
    this.speechDuration = 0
    this.userHeard = false
    this.userSeen = false
    this.periodicVerificationFailures = 0
    this.transcriptVerificationFailed = false
    this.transcriptVerificationTimeout = null
  }

  get messages() {
    const array = Array.from(this._messages.values())

    const chatController = this.meeting.chatController
    if (chatController) {
      for (const model of chatController.messagesCol.models) {
        if (model.data.type !== FirestoreRoomMessageType.Text) continue
        const ts = model.data.createdAt.getTime()
        if (ts < this._startedAt) continue
        array.push({
          id: ts,
          identity: model.data.authorId,
          transcript: model.data.text,
          final: true,
          finalSort: ts,
          isChat: true,
        })
      }
    }

    return array.sort(sortTranscriptMessages)
  }
}
